import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CLIENT_CONFIG } from '../../core/client/buildConfig'
import { modelsFunctions } from '../../api/modules/modelsApi'
import { AIModel, UserModelSettings } from '../../types/modelTypes'

const MODELS_QUERY_KEY = ['models']
const MODEL_SETTINGS_QUERY_KEY = ['model-settings']

const MODEL_SELECTION_ENABLED = CLIENT_CONFIG.features.model_selection === true

export function useModels() {
  const queryClient = useQueryClient()

  const { data: models = [], isLoading: isLoadingModels } = useQuery<AIModel[]>({
    queryKey: MODELS_QUERY_KEY,
    queryFn: () => modelsFunctions.fetchModels('completion'),
    staleTime: 5 * 60 * 1000,
    retry: 2,
    enabled: MODEL_SELECTION_ENABLED,
  })

  const { data: settingsResponse, isLoading: isLoadingSettings } = useQuery({
    queryKey: MODEL_SETTINGS_QUERY_KEY,
    queryFn: () => modelsFunctions.fetchUserModelSettings(),
    staleTime: 5 * 60 * 1000,
    retry: 2,
    enabled: MODEL_SELECTION_ENABLED,
  })

  const userSettings: Record<string, UserModelSettings> = {}
  if (settingsResponse?.items) {
    for (const item of settingsResponse.items) {
      userSettings[item.ai_model_id] = item
    }
  }

  const updateSettingsMutation = useMutation({
    mutationFn: ({ aiModelId, settings }: { aiModelId: string; settings: Record<string, number | string> }) =>
      modelsFunctions.updateUserModelSettings(aiModelId, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MODEL_SETTINGS_QUERY_KEY })
    },
  })

  return {
    models,
    userSettings,
    isLoading: MODEL_SELECTION_ENABLED && (isLoadingModels || isLoadingSettings),
    updateSettings: updateSettingsMutation.mutate,
    isUpdating: updateSettingsMutation.isPending,
    isUpdateError: updateSettingsMutation.isError,
  }
}
